// ── GENERIC CASH WHEAT TARGETS — "where should I set targets for wheat?" ────────────────────
//
// Kevin, 2026-09-21: the Full Strategy Report should end with "generic recommended cash targets,
// nothing to do with what they have working, just a technical response to 'where should I set
// targets for wheat?'"
//
// ⚠ THIS REVERSES A DELIBERATE REMOVAL, AND THE SHAPE IS WHAT KEEPS IT HONEST. On 2026-09-01 the
// report's "Marketing plan" page was retired for being "model-generated levels handed to a farmer
// on paper as instructions — a when-to-sell recommendation in print", and the rule written down
// was "Silas administers a farmer's target; it does not author one." Kevin has overturned that
// for a GENERIC ladder. So nothing here is authored: every number is either
//   • a FUTURES level the ladder engine measured (grainTargets.js — the distance this board's own
//     last five years cleared 75% / 40% / 18% of the time, snapped to a real level), or
//   • a BASIS the elevator POSTED, read off grain_basis_board with its sheet version and date.
// The words are levels and their history, never "set order at" or "sell into".
//
// ⚠ AND IT NEVER RANKS A BUYER. The standing rule (CLAUDE.md, "SILAS DOES NOT TELL A FARMER WHERE
// TO SELL") forbids naming a best elevator or sorting posted bids by price. Points are listed as
// the sheet posts them and the cash figure is a RANGE, never a winner.
//
// ⚠ DELIVERED AND PICKED UP ARE NOT ONE RANGE. The difference between them is freight, which this
// report does not know, so mixing them into one low-to-high spread ranks incomparable things —
// the same defect the Grain Summary already records. They are separate rows, each labelled.

export const CASH_TARGET_ABSENT = {
  NO_RUNGS: "no measured target ladder for this board",
  NO_BASIS: "no posted basis for this delivery month",
};

const n = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const r2 = (v) => (v == null ? null : Math.round(v * 100) / 100);

// ⚠ THE TWO SIDES SPELL THE CONTRACT DIFFERENTLY, AND THE MISMATCH IS SILENT. The futures
// history calls July 2027 wheat **WN27** (commodity letter first); the elevator's board posts the
// same month as **N27**. Compared literally they never match, and the page would have reported
// "no posted basis" forever while the basis sat right there — the Howlett-slug class of defect.
// The month letter plus the two-digit year is what both sides agree on, so that is the key.
export function contractKey(sym) {
  const s = String(sym || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const m = /([FGHJKMNQUVXZ])(\d{2})$/.exec(s);
  return m ? m[1] + m[2] : null;
}

// The posted basis for ONE futures month, grouped by delivery term. Rows are the board's own
// (grain_basis_board): { commodity, location, term, cme_contract, ship_month, basis }.
export function basisForContract(rows, contractSym, commodity = "wheat") {
  const want = contractKey(contractSym);
  if (!want) return [];
  return (rows || [])
    .filter((r) => String(r.commodity || "").toLowerCase() === String(commodity).toLowerCase())
    .filter((r) => contractKey(r.cme_contract) === want)
    .filter((r) => n(r.basis) != null)
    // ⚠ THE SHEET STAMP TRAVELS WITH THE ROW IT CAME FROM. Read off "whichever board row happened
    // to carry a version" it named a CORN sheet, two days older, under a wheat basis — a
    // provenance line that cannot be checked against the sheet it claims.
    .map((r) => ({ location: r.location || null, term: (r.term || "").toLowerCase() || null,
      shipMonth: r.ship_month || null, basis: n(r.basis),
      sheetVersion: r.sheet_version || null, sheetUpdated: r.sheet_updated || null }));
}

// One group per delivery term, each with the range of posted basis and the points behind it.
// A term with ONE posted point is a point, not a range, and says so by low === high.
export function basisTerms(points) {
  const by = {};
  for (const p of points || []) {
    const k = p.term || "unstated";
    (by[k] = by[k] || { term: k, points: [], low: null, high: null }).points.push(p);
  }
  for (const g of Object.values(by)) {
    const vals = g.points.map((p) => p.basis);
    g.low = r2(Math.min(...vals));
    g.high = r2(Math.max(...vals));
  }
  // Stable order that is NOT a ranking by price: delivered first because it is the common case,
  // then pickup, then anything the sheet did not label.
  const rank = { delivered: 0, pickup: 1, unstated: 2 };
  return Object.values(by).sort((a, b) => (rank[a.term] ?? 9) - (rank[b.term] ?? 9));
}

// ── THE LADDER IN CASH ───────────────────────────────────────────────────────────────────────
// `rungs` are grainTargets' own: { lvl, fill, label }. Nothing is recomputed here — a second
// ladder is a second answer, and this file is a translation, not an opinion.
// ⚠ A RUNG CARRIES TWO NAMES AND THEY ARE NOT THE SAME NAME. grainTargets writes
// { lvl, fill, kind, lbl }: `kind` is the TIER — "attainable" / "strong" / "flyer", Kevin's own
// three — and `lbl` is the real level the aim was snapped to ("1-mo high", "round number").
// Reading `label` printed both blank; reading `lbl` as the tier printed "1-mo high" where the
// page meant "attainable". The tier answers which order to place, the level says why it is there,
// so the page prints both.
const rungTier = (t) => t.kind || t.label || null;
const rungLevel = (t) => t.lbl || null;

export function wheatCashTargets({ rungs = [], basisRows = [], contractSym = null, contractLabel = null,
  boardLast = null, asOf = null, sheetVersion = null, sheetUpdated = null, commodity = "wheat" } = {}) {
  const usable = (rungs || []).filter((t) => n(t.lvl != null ? t.lvl : t.price) != null);
  if (!usable.length) return { ok: false, absent: CASH_TARGET_ABSENT.NO_RUNGS, contractLabel, rows: [], terms: [] };

  const points = basisForContract(basisRows, contractSym, commodity);
  const terms = basisTerms(points);
  if (!terms.length) {
    // ⚠ NO BASIS IS NOT A ZERO BASIS. A futures level printed as though it were cash is the
    // error this whole page exists to avoid, so the board levels are returned and NAMED as
    // board levels — never silently converted at a basis nobody posted.
    return { ok: false, absent: CASH_TARGET_ABSENT.NO_BASIS, contractLabel, contractSym,
      boardLast: n(boardLast), asOf, rows: usable.map((t) => ({ label: rungTier(t), level: rungLevel(t),
        futures: r2(n(t.lvl != null ? t.lvl : t.price)), fill: n(t.fill), cash: [] })), terms: [] };
  }

  const rows = usable.map((t) => {
    const f = r2(n(t.lvl != null ? t.lvl : t.price));
    return {
      label: rungTier(t), level: rungLevel(t),
      futures: f,
      fill: n(t.fill),
      // One cash range PER TERM. Freight is unknown, so these are never merged.
      cash: terms.map((g) => ({ term: g.term, low: r2(f + g.low), high: r2(f + g.high),
        basisLow: g.low, basisHigh: g.high, points: g.points.length })),
    };
  });
  // The sheet the MATCHED rows came from — never a stamp read off some other row of the board.
  const stamp = points.find((p) => p.sheetVersion || p.sheetUpdated) || {};
  return { ok: true, contractLabel, contractSym, boardLast: n(boardLast), asOf,
    sheetVersion: stamp.sheetVersion || sheetVersion || null,
    sheetUpdated: stamp.sheetUpdated || sheetUpdated || null, rows, terms };
}
